import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, AlertTriangle } from 'lucide-react';
import proApiService from '../../services/proApiService';

const DisputeForm = ({ projectId, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    dispute_type: '',
    description: '',
    disputed_amount: '',
    insurance_company: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const disputeTypes = [
    { value: 'appraisal', label: 'Appraisal Dispute' },
    { value: 'scope', label: 'Scope of Work Dispute' },
    { value: 'cost', label: 'Cost Estimate Dispute' },
    { value: 'timeline', label: 'Timeline Dispute' },
    { value: 'coverage', label: 'Coverage Dispute' },
    { value: 'depreciation', label: 'Depreciation Dispute' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const disputeData = {
        project_id: projectId,
        dispute_type: formData.dispute_type,
        description: formData.description,
        disputed_amount: formData.disputed_amount ? parseFloat(formData.disputed_amount) : null,
        insurance_company: formData.insurance_company
      };

      const result = await proApiService.createDispute(disputeData);
      onSubmit(result);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create dispute');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
          File Insurance Dispute
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Dispute Type */}
          <div className="space-y-2">
            <Label htmlFor="dispute_type">Dispute Type *</Label>
            <Select onValueChange={(value) => handleInputChange('dispute_type', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select dispute type" />
              </SelectTrigger>
              <SelectContent>
                {disputeTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Insurance Company */}
          <div className="space-y-2">
            <Label htmlFor="insurance_company">Insurance Company</Label>
            <Input
              id="insurance_company"
              placeholder="e.g., State Farm, Allstate, Farmers"
              value={formData.insurance_company}
              onChange={(e) => handleInputChange('insurance_company', e.target.value)}
            />
          </div>

          {/* Disputed Amount */}
          <div className="space-y-2">
            <Label htmlFor="disputed_amount">Disputed Amount ($)</Label>
            <Input
              id="disputed_amount"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.disputed_amount}
              onChange={(e) => handleInputChange('disputed_amount', e.target.value)}
            />
            <p className="text-sm text-gray-600">
              Enter the dollar amount in dispute (optional)
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Dispute Description *</Label>
            <Textarea
              id="description"
              placeholder="Provide detailed description of the dispute, including specific issues, documentation references, and desired resolution..."
              rows={6}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              required
            />
            <p className="text-sm text-gray-600">
              Be specific about the issue and include any relevant details
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700"
              disabled={loading || !formData.dispute_type || !formData.description}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Filing Dispute...
                </>
              ) : (
                'File Dispute'
              )}
            </Button>
          </div>
        </form>

        {/* Help Section */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-medium text-blue-900 mb-2">Dispute Filing Tips:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Include specific claim numbers and reference documentation</li>
            <li>• Clearly state your position and supporting evidence</li>
            <li>• Reference industry standards and local building codes</li>
            <li>• Include photos, measurements, and expert opinions when available</li>
            <li>• Be professional and factual in your description</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default DisputeForm;